import { Icon } from '@/components/Icon'
import { IconButton } from '@/components/IconButton'
import { Modal } from '@/components/Modal'
import { SearchSelect } from '@/components/selects/SearchSelect'
import { Select, type SelectRefType } from '@/components/selects/Select'
import { Space } from '@/components/Space'
import { FormattedTitleWithAddress } from '@/components/storeLocations/FormattedTitleWithAddress'
import { StoreLocationSearchSelect } from '@/components/storeLocations/StoreLocationSearchSelect'
import { Typography } from '@/components/Typography'
import { useGetStoreFolders } from '@/queries/storeFolders/useGetStoreFolders'
import { useGetStoreLocations } from '@/queries/storeLocations/useGetStoreLocations'
import { ReportDataType } from '@/services/types/notifications/mockNotifications'
import { AvailableProviders } from '@/utils/constants/providers/availableProviders'
import { DeleteFilled, ExclamationCircleOutlined } from '@ant-design/icons'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { type FilterOptions, I18N_PREFIX } from '../CustomFilters'

import styles from './styles.module.scss'

export enum EmailReportFilterEvaluation {
  contains = 'contains', // an array or string has a value inside
  notContains = 'notContains', // an array or string does not have a value inside
}

export enum EmailReportFilterSource {
  location = 'location',
  folder = 'folder',
  publisher = 'publisher',
}

type FilterKeyType<T extends EmailReportFilterSource> = T extends EmailReportFilterSource.location
  ? 'stores'
  : T extends EmailReportFilterSource.folder
    ? 'folders'
    : T extends EmailReportFilterSource.publisher
      ? 'publishers'
      : T

type FilterContentType = string[]

type ContentFilterShape<T extends EmailReportFilterSource> = {
  [K in FilterKeyType<T>]: FilterContentType
}

type FilterProps<T extends EmailReportFilterSource> = T extends
  | EmailReportFilterSource.location
  | EmailReportFilterSource.folder
  | EmailReportFilterSource.publisher
  ? {
      source: T
      evaluation: EmailReportFilterEvaluation
    } & ContentFilterShape<T>
  : never

export type EmailReportFilterPropsUnion =
  | FilterProps<EmailReportFilterSource.location>
  | FilterProps<EmailReportFilterSource.folder>
  | FilterProps<EmailReportFilterSource.publisher>

function getDefaultFilterValueBySource(
  source: EmailReportFilterSource,
): EmailReportFilterPropsUnion | null {
  switch (source) {
    case EmailReportFilterSource.location:
      return {
        source: EmailReportFilterSource.location,
        evaluation: EmailReportFilterEvaluation.contains,
        stores: [],
      }
    case EmailReportFilterSource.folder:
      return {
        source: EmailReportFilterSource.folder,
        evaluation: EmailReportFilterEvaluation.contains,
        folders: [],
      }
    case EmailReportFilterSource.publisher:
      return {
        source: EmailReportFilterSource.publisher,
        evaluation: EmailReportFilterEvaluation.contains,
        publishers: [],
      }
    default:
      return null
  }
}

interface PublisherOptionProps {
  label: string
  value: AvailableProviders
}

interface FilterComponentProps<T extends EmailReportFilterSource> {
  filter?: FilterProps<T>
  onChange?: (filter: EmailReportFilterPropsUnion) => void
  onDelete: (index: number | number[]) => void
  id?: string
  filterOptions: FilterOptions
  selectedReports?: ReportDataType[]
}

const ANALYTICS_PUBLISHERS: PublisherOptionProps[] = [
  {
    label: 'Bing',
    value: AvailableProviders.Bing,
  },
  {
    label: 'Facebook',
    value: AvailableProviders.Facebook,
  },
  {
    label: 'Google',
    value: AvailableProviders.Google,
  },
  {
    label: 'Yelp',
    value: AvailableProviders.Yelp,
  },
]
const ANALYSIS_PUBLISHERS: PublisherOptionProps[] = [
  {
    label: 'Doordash',
    value: AvailableProviders.Doordash,
  },
  {
    label: 'EzCater',
    value: AvailableProviders.EzCater,
  },
  {
    label: 'Facebook',
    value: AvailableProviders.Facebook,
  },
  {
    label: 'Google',
    value: AvailableProviders.Google,
  },
  {
    label: 'Grubhub',
    value: AvailableProviders.Grubhub,
  },
  {
    label: 'OpenTable',
    value: AvailableProviders.OpenTable,
  },
  {
    label: 'TripAdvisor',
    value: AvailableProviders.TripAdvisor,
  },
  {
    label: 'Uber Eats',
    value: AvailableProviders.UberEats,
  },
  {
    label: 'Yelp',
    value: AvailableProviders.Yelp,
  },
]

const ALL_PUBLISHER_OPTIONS: PublisherOptionProps[] = Array.from(
  new Map(
    [...ANALYTICS_PUBLISHERS, ...ANALYSIS_PUBLISHERS].map((publisher) => [
      publisher.value,
      publisher,
    ]),
  ).values(),
)

function getAvailablePublishers(selectedReports: ReportDataType[]) {
  const isAnalyticsSelected = selectedReports.includes(ReportDataType.analytics)
  const isReviewAnalysisSelected = selectedReports.some((report) =>
    [
      ReportDataType.reviewAnalysis,
      ReportDataType.sentimentAnalysis,
      ReportDataType.keywordAnalysis,
    ].includes(report),
  )

  if (isAnalyticsSelected && isReviewAnalysisSelected) {
    return ALL_PUBLISHER_OPTIONS
  } else if (isAnalyticsSelected) {
    return ANALYTICS_PUBLISHERS
  } else if (isReviewAnalysisSelected) {
    return ANALYSIS_PUBLISHERS
  } else {
    return []
  }
}

export function FilterComponent<T extends EmailReportFilterSource>({
  filter,
  onChange: onFilterChange,
  onDelete,
  id,
  filterOptions,
  selectedReports,
}: FilterComponentProps<T>) {
  const [filterIndexToDelete, setFilterIndexToDelete] = useState<number | null>(null)
  const [availablePublishers, setAvailablePublishers] = useState<PublisherOptionProps[]>([])

  const publisherSelectOptionsRef = useRef<SelectRefType>(null)
  const hasPreviousSelectedReports = useRef<boolean>(selectedReports?.length !== 0)
  const isAlreadyAutoFocused = useRef<boolean>(false)

  const storeLocations = useGetStoreLocations()
  const storeLocationsOptions =
    storeLocations.data?.map((location) => ({
      label: <FormattedTitleWithAddress location={location} />,
      value: location.id,
      selectedStateLabel: location.title,
    })) || []

  const storeFolders = useGetStoreFolders()
  const storeFoldersOptions =
    storeFolders.data?.map((folder) => ({
      label: folder.title,
      value: folder.id,
    })) || []

  const { t } = useTranslation()

  const FILTER_SOURCE_TO_OPTION_MAPPER = useMemo(
    () => ({
      location: {
        label: t(`${I18N_PREFIX}.filterLabels.locations`),
        value: EmailReportFilterSource.location,
      },
      folder: {
        label: t(`${I18N_PREFIX}.filterLabels.folders`),
        value: EmailReportFilterSource.folder,
      },
      publisher: {
        label: t(`${I18N_PREFIX}.filterLabels.publishers`),
        value: EmailReportFilterSource.publisher,
      },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  )

  function handleSelectedFilterChange(source: EmailReportFilterSource) {
    const newFilter = getDefaultFilterValueBySource(source)

    if (!newFilter) return

    onFilterChange?.(newFilter)
  }

  useEffect(() => {
    hasPreviousSelectedReports.current = selectedReports?.length !== 0
  }, [selectedReports])

  useEffect(() => {
    if (!Array.isArray(selectedReports)) return

    const availablePublishers = getAvailablePublishers(selectedReports)

    setAvailablePublishers(availablePublishers)

    if (publisherSelectOptionsRef.current) {
      const { change, value } = publisherSelectOptionsRef.current

      if (Array.isArray(value) && value.length > 0 && hasPreviousSelectedReports.current) {
        const filteredSelectedPublishers = value.filter((selectedPublisher) => {
          return availablePublishers.find((publisher) => publisher.value === selectedPublisher)
        })

        change(filteredSelectedPublishers)
        hasPreviousSelectedReports.current = selectedReports.length !== 0
      }
    }
  }, [selectedReports])

  // Control autofocus on store locations select
  const storeLocationsSelectOptionsRef = useRef<SelectRefType>(null)
  useEffect(() => {
    if (
      !storeLocationsSelectOptionsRef.current ||
      filter?.source !== EmailReportFilterSource.location
    )
      return

    if (filter.stores.length === 0 && !storeLocations.isPending && !isAlreadyAutoFocused.current) {
      storeLocationsSelectOptionsRef.current.focus()

      isAlreadyAutoFocused.current = true
    }
  }, [storeLocations.isPending, filter])

  // Control autofocus on store folders select
  const storeFoldersSelectOptionsRef = useRef<SelectRefType>(null)
  useEffect(() => {
    if (!storeFoldersSelectOptionsRef.current || filter?.source !== EmailReportFilterSource.folder)
      return

    if (filter.folders.length === 0 && !storeFolders.isPending && !isAlreadyAutoFocused.current) {
      storeFoldersSelectOptionsRef.current.focus()

      isAlreadyAutoFocused.current = true
    }
  }, [storeFolders.isPending, filter])

  if (!filter) return null

  const filterIndex = Number(id?.split('_').at(-1))

  return (
    <div id={id}>
      <Space direction="vertical">
        <div className={styles.container}>
          <Select
            options={[FILTER_SOURCE_TO_OPTION_MAPPER[filter.source], ...filterOptions]}
            defaultValue={FILTER_SOURCE_TO_OPTION_MAPPER[filter.source].value}
            onChange={handleSelectedFilterChange}
            dropdownMatchSelectWidth={false}
            style={{ width: 'fit-content' }}
          />

          {filter.source === EmailReportFilterSource.location && (
            <>
              <Select
                defaultValue={filter.evaluation}
                options={[
                  {
                    label: t(`${I18N_PREFIX}.evaluationLabels.include`),
                    value: 'contains',
                  },
                  {
                    label: t(`${I18N_PREFIX}.evaluationLabels.notInclude`),
                    value: 'notContains',
                  },
                ]}
                dropdownMatchSelectWidth={false}
                style={{ width: 'fit-content' }}
                onChange={(evaluation: EmailReportFilterEvaluation) => {
                  onFilterChange?.({
                    ...filter,
                    evaluation,
                  })
                }}
              />

              <StoreLocationSearchSelect
                {...(filter.stores.length === 0 && {
                  autoFocus: true,
                })}
                ref={storeLocationsSelectOptionsRef}
                style={{ width: '100%' }}
                options={storeLocationsOptions}
                placeholder={
                  storeLocations.isPending
                    ? t('utils.loading')
                    : t(`${I18N_PREFIX}.locations.selectPlaceholder`)
                }
                value={storeLocations.isPending ? [] : filter.stores}
                disabled={storeLocations.isPending}
                onChange={(storesIds: string[]) => {
                  onFilterChange?.({
                    ...filter,
                    stores: storesIds,
                  })
                }}
                countLabel={t('utils.locations')}
              />
            </>
          )}

          {filter.source === EmailReportFilterSource.folder && (
            <>
              <Select
                defaultValue={filter.evaluation}
                options={[
                  {
                    label: t(`${I18N_PREFIX}.evaluationLabels.include`),
                    value: 'contains',
                  },
                  {
                    label: t(`${I18N_PREFIX}.evaluationLabels.notInclude`),
                    value: 'notContains',
                  },
                ]}
                dropdownMatchSelectWidth={false}
                style={{ width: 'fit-content' }}
                onChange={(evaluation: EmailReportFilterEvaluation) =>
                  onFilterChange?.({
                    ...filter,
                    evaluation,
                  })
                }
              />

              <SearchSelect
                {...(filter.folders.length === 0 && {
                  autoFocus: true,
                })}
                ref={storeFoldersSelectOptionsRef}
                options={storeFoldersOptions}
                placeholder={
                  storeFolders.isPending
                    ? t('utils.loading')
                    : t(`${I18N_PREFIX}.folders.selectPlaceholder`)
                }
                disabled={storeFolders.isPending}
                style={{ width: '100%' }}
                onChange={(foldersIds: string[]) => {
                  onFilterChange?.({
                    ...filter,
                    folders: foldersIds,
                  })
                }}
                value={storeFolders.isPending ? [] : filter.folders}
                countLabel={t('utils.folders')}
              />
            </>
          )}

          {filter.source === EmailReportFilterSource.publisher && (
            <>
              <Select
                defaultValue={filter.evaluation}
                options={[
                  {
                    label: t(`${I18N_PREFIX}.evaluationLabels.include`),
                    value: 'contains',
                  },
                  {
                    label: t(`${I18N_PREFIX}.evaluationLabels.notInclude`),
                    value: 'notContains',
                  },
                ]}
                dropdownMatchSelectWidth={false}
                style={{ width: 'fit-content' }}
                onChange={(evaluation: EmailReportFilterEvaluation) =>
                  onFilterChange?.({
                    ...filter,
                    evaluation,
                  })
                }
              />

              <SearchSelect
                {...(filter.publishers.length === 0 && {
                  autoFocus: true,
                })}
                ref={publisherSelectOptionsRef}
                options={availablePublishers}
                placeholder={t(`${I18N_PREFIX}.publishers.selectPlaceholder`)}
                style={{ width: '100%' }}
                onChange={(publishersIds) => {
                  onFilterChange?.({
                    ...filter,
                    publishers: publishersIds,
                  })
                }}
                value={filter.publishers}
                countLabel={t('utils.publishers')}
              />
            </>
          )}

          <IconButton
            icon={<Icon color="secondary-text" icon={<DeleteFilled />} />}
            onClick={() => {
              setFilterIndexToDelete(filterIndex)
            }}
            style={{ minWidth: '2.25rem' }}
          />
        </div>
      </Space>

      <Modal
        open={filterIndexToDelete !== null}
        onCancel={() => setFilterIndexToDelete(null)}
        okText={t(`${I18N_PREFIX}.deleteFilterConfirmationModal.ok`)}
        cancelText={t(`${I18N_PREFIX}.deleteFilterConfirmationModal.cancel`)}
        onOk={() => {
          if (filterIndexToDelete === null) return

          onDelete(filterIndexToDelete)
          setFilterIndexToDelete(null)
        }}
        bodyStyle={{ padding: '2rem 3rem 1.5rem 4.375rem' }}
        className={styles.deleteFilterConfirmationModal}
      >
        <Space direction="vertical">
          <div className={styles.deleteFilterConfirmationModalIcon}>
            <Icon color="warning" size="1.5rem" icon={<ExclamationCircleOutlined />} />
          </div>

          <Typography.Title level={5}>
            {t(`${I18N_PREFIX}.deleteFilterConfirmationModal.title`)}
          </Typography.Title>

          <Typography.Text>
            {t(`${I18N_PREFIX}.deleteFilterConfirmationModal.content`)}
          </Typography.Text>
        </Space>
      </Modal>
    </div>
  )
}
